import { Component, OnInit } from '@angular/core'
import { ActivatedRoute } from '@angular/router'
import { Store } from '@ngrx/store'
import { manageSubscriptionActions } from './store/manage-subscription.actions'
import {
  Controls,
  CustomFieldValue,
  selectControls,
  selectCustomFields,
  SelectNewsOrThemen,
  selectStatus,
  selectUserData,
  StateStatus,
  UserData,
} from './store/manage-subscription.state'
import { Observable } from 'rxjs'
import { state, style, trigger } from '@angular/animations'
import { environment } from '../../../environments/environment'
import { MatDialog } from '@angular/material/dialog'
import { ConfirmModalComponent } from './components/confirm-modal/confirm-modal.component'
import { TranslateService } from '@ngx-translate/core'

@Component({
  selector: 'cover-manage-subscription',
  templateUrl: './manage-subscription.component.html',
  styleUrls: ['./manage-subscription.component.scss'],
  animations: [
    trigger('openClose', [
      state(
        'open',
        style({
          height: 'fit-content',
          opacity: 1,
        }),
      ),
      state(
        'close',
        style({
          height: 0,
          opacity: 0.8,
        }),
      ),
    ]),
  ],
})
export class ManageSubscriptionComponent implements OnInit {
  userData$?: Observable<UserData>
  customFields$?: Observable<CustomFieldValue[]>
  pageStatus$?: Observable<StateStatus>
  controls$?: Observable<Controls>
  private userId: string = ''

  readonly firstNameId = environment.firstNameId
  readonly lastNameId = environment.lastNameId
  readonly birthdayId = environment.birthdayId
  readonly doiId = environment.doiId
  readonly uwg7Id = environment.uwg7Id
  readonly shouldUseCustomButton = environment.useCustomUnsubscriptionButton
  readonly unsubscriptionButtonLink = environment.customUnsubscriptionButtonLink
  readonly companyName = environment.customer

  constructor(
    private route: ActivatedRoute,
    private store: Store,
    private dialog: MatDialog,
    private translate: TranslateService,
  ) {}

  ngOnInit(): void {
    this.route.queryParams.subscribe({
      next: params => {
        this.userId = this.cleanedParam(params['user-id'])
        this.store.dispatch(manageSubscriptionActions.tryLoadData({ payload: { userId: this.userId } }))
        const lang = params['lang']
        if (lang) this.translate.use(lang)
      },
    })

    this.userData$ = this.store.select(selectUserData)
    this.controls$ = this.store.select(selectControls)
    this.customFields$ = this.store.select(selectCustomFields)
    this.pageStatus$ = this.store.select(selectStatus)
  }

  cleanedParam(param: string): string {
    return param.slice(9)
  }

  toggleAbsenderSelection(aId: string): void {
    this.store.dispatch(manageSubscriptionActions.toggleAbsenderSelection({ payload: { aId } }))
  }

  toggleThemenSelection(aId: string, tId: string): void {
    this.store.dispatch(manageSubscriptionActions.toggleThemenSelection({ payload: { aId, tId } }))
  }

  toggleOptionOpen(index: number): void {
    this.store.dispatch(manageSubscriptionActions.toggleOption({ payload: { index } }))
  }

  submitFormInfo(
    selectedFields: SelectNewsOrThemen[],
    customFields: CustomFieldValue[],
    userCustomFields: CustomFieldValue[],
  ): void {
    const subscriptionFields = customFields
      .filter(c => selectedFields.find(i => i.id === c.customFieldId)?.isSelected)
      .map(c => {
        const newValues = c.values.filter(value =>
          selectedFields
            .find(i => i.id === c.customFieldId)
            ?.themenSelected.filter(t => t.isSelected)
            .map(t => t.id)
            .includes(value),
        )

        return {
          ...c,
          value: newValues,
          values: newValues,
        }
      })
    const doi = userCustomFields.find(c => c.customFieldId === this.doiId)
    const uwg7 = userCustomFields.find(c => c.customFieldId === this.uwg7Id)

    const unsubscribeCover =
      subscriptionFields.length === 0 &&
      (!doi || doi.value[0].toLocaleLowerCase() === 'false') &&
      (!uwg7 || uwg7.value[0].toLocaleLowerCase() === 'false')
    if (unsubscribeCover) {
      const dialogRef = this.dialog.open(ConfirmModalComponent)
      dialogRef.afterClosed().subscribe(confirmation => {
        if (confirmation)
          this.store.dispatch(manageSubscriptionActions.trySaveUserPref({ payload: { unsubscribeCover } }))
      })
    } else {
      this.store.dispatch(manageSubscriptionActions.trySaveUserPref({ payload: { unsubscribeCover } }))
    }
  }

  toggleOriginCustomField(customFieldId: string): void {
    this.store.dispatch(manageSubscriptionActions.toggleUserOrigin({ payload: { customFieldId } }))
  }

  onOriginCustomFieldSelected(customFields: CustomFieldValue[], id: string): boolean {
    const field = customFields.find(f => f.customFieldId === id)
    if (!field) return false
    return field.value[0]?.toLowerCase() === 'true'
  }

  getCustomFieldValue(customFields: CustomFieldValue[], name: string): string {
    const field = customFields.find(f => f.name === name)
    if (!field || field.value.length === 0) return ''
    return field.value[0]
  }

  getCustomFieldValueById(customFields: CustomFieldValue[], id: string): string {
    const field = customFields.find(f => f.customFieldId === id)
    if (!field || field.value.length === 0) return ''
    return field.value[0]
  }

  filterSubscriptionWithoutThemes(customFields: CustomFieldValue[]): CustomFieldValue[] {
    //@ts-ignore
    return customFields.filter(f => !environment.themeFields.includes(f.customFieldId))
  }

  filterSubscriptionThemes(customFields: CustomFieldValue[]): CustomFieldValue[] {
    //@ts-ignore
    return customFields.filter(f => environment.themeFields.includes(f.customFieldId))
  }

  isIntermediate(control: SelectNewsOrThemen): boolean {
    if (control.themenSelected.every(t => !t.isSelected)) return false
    return control.themenSelected.some(t => !t.isSelected)
  }

  unmarkAll(): void {
    const dialogRef = this.dialog.open(ConfirmModalComponent)
    dialogRef.afterClosed().subscribe(confirmation => {
      if (confirmation) {
        this.store.dispatch(manageSubscriptionActions.unsubscribe({ payload: undefined }))
      }
    })
  }

  getRelativeIndexOfThemes(index: number, customFields: CustomFieldValue[]) {
    return this.filterSubscriptionWithoutThemes(customFields).length + index
  }
}
